import { SgProductService, SgSessionService, SgSubmissionUtil } from 'saga-modules';
import { SgCardService } from 'sg-capability-card';
import CommonConstants from 'sg-common-constants';
import _ from 'lodash';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import SgQnBLobSpecificUtil from 'sg-qnb-lob-specific-util';

const FIVE_POUND = 5;
const ZERO_POUND = 0;
const ANNUAL_PAYMENT_PLAN_CODES = ['annualupfront', 'annual'];
const QUARTERLY_PAYMENT_PLAN_CODES = ['quarterlyupfront', 'quarterly'];
const prodEnvironment = !(window.dtrum === undefined);

/**
 * Check if increase of amount is equal or less then five pound
 * @param {int} amount
 *
 * @returns {boolean}
 */
function isIncreaseLessThanOrEqualsFivePound(amount) {
    return amount <= FIVE_POUND && amount > ZERO_POUND;
}

/**
 * Check if refund of amount is equal or less then five pound
 * @param {int} amount
 *
 * @returns {boolean}
 */
function isRefundLessThanOrEqualsFivePound(amount) {
    return amount >= -FIVE_POUND && amount < ZERO_POUND;
}

/**
 * Check if payment plan is annual or annual upfront
 * @param {String} paymentPlanCode
 *
 * @returns {boolean}
 */
function isAnnualPaymentPlan(paymentPlanCode) {
    return ANNUAL_PAYMENT_PLAN_CODES.includes(paymentPlanCode);
}

/**
 * Check if payment method is credit card
 * @param {String} paymentMethodCode
 *
 * @returns {boolean}
 */
function isCreditCardPaymentMethod(paymentMethodCode) {
    return CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD === paymentMethodCode;
}

/**
 * Check if is annual payment plan and credit card
 * @param {String} paymentPlanCode
 * @param {String} paymentMethodCode
 *
 * @returns {boolean}
 */
function isAnnualCreditCardPayment(paymentPlanCode, paymentMethodCode) {
    return isAnnualPaymentPlan(paymentPlanCode) && isCreditCardPaymentMethod(paymentMethodCode);
}

/**
 * Check if payment plan is quarterly or quarterly upfront
 * @param {String} paymentPlanCode
 * @param {String} paymentMethodCode
 * @returns {boolean}
 */
function isQuarterlyCCPaymentPlan(paymentPlanCode, paymentMethodCode) {
    return QUARTERLY_PAYMENT_PLAN_CODES.includes(paymentPlanCode) && CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD === paymentMethodCode;
}

/**
 * Check if we should show CPA message
 * @param {String} paymentPlanCode
 * @param {String} paymentMethodCode
 * @returns {boolean}
 */
function shouldShowCPAMessage(paymentPlanCode, paymentMethodCode) {
    return QUARTERLY_PAYMENT_PLAN_CODES.includes(paymentPlanCode) && CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD === paymentMethodCode;
}

/**
 * Check if immediate payment is required
 * @param {String} paymentPlanCode
 * @param {String} paymentMethodCode
 * @param {int} transactionCost
 *
 * @returns {boolean}
 */
function isImmediatePaymentRequired(paymentPlanCode, paymentMethodCode, transactionCost) {
    return isAnnualCreditCardPayment(paymentPlanCode, paymentMethodCode) && transactionCost > FIVE_POUND;
}

function isDirectDebit(paymentMethodCode) {
    return paymentMethodCode === CommonConstants.PAYMENT_METHOD_CODES.DIRECT_DEBIT;
}

/**
 * @param {Object} plan Payment plan
 * @returns {boolean} True if an Annual Payment Plan
 */
function isPaymentPlanAnnual(plan) {
    return plan.numberOfInstallments_Ext <= 1;
}

/*
 * Build Customer details params to load Iframe
 */
function buildCustomerDataForIframe(address, email, firstName, lastName, isModelValue) {
    const addressLine1 = isModelValue ? address.addressLine1 : address.addressLine1.value;
    const addressLine2 = isModelValue ? address.addressLine2 : address.addressLine2.value;
    const addressLine3 = isModelValue ? address.addressLine3 : address.addressLine3.value;
    return {
        email: isModelValue ? email : email.value,
        addressPostalCode: isModelValue ? address.postalCode : address.postalCode.value,
        addressCountry: isModelValue ? address.country : address.country.value,
        addressCity: isModelValue ? address.city : address.city.value,
        addressLine: `${addressLine1 || addressLine2} ${addressLine3 || null}`,
        surName: isModelValue ? lastName : lastName.value,
        firstName: isModelValue ? firstName : firstName.value,
    };
}

/*
 * Returns Customer details params based on AccountHolder
 */
function buildCustomerData(modelVM) {
    const accountHolder = SgSubmissionUtil.getAccountHolder(modelVM);
    return buildCustomerDataForIframe(accountHolder.primaryAddress, accountHolder.emailAddress1, accountHolder.firstName, accountHolder.lastName);
}

/*
 * Returns Customer device details ipAddress, screenHeight and screenWidth
 */
const customerDeviceDetails = (data) => {
    return [data.customerIpAddress, data.customerBrowserScreenHeight, data.customerBrowserScreenWidth];
};

/**
 * Get Barclaycard for iframe generation
 */

function barclayCardIframe(cardData, customerData, isArrears, setIsDataLoaded) {
    if (cardData.amount === 0 || !isArrears) {
        _.set(cardData, 'amount', cardData.amount.toFixed(2));
    }

    const params = {
        unsigned_field_names: cardData.unsignedFieldNames,
        amount: cardData.amount,
        bill_to_address_postal_code: customerData.addressPostalCode,
        signature: cardData.signature,
        transaction_uuid: cardData.transactionUid,
        signed_field_names: cardData.signedFieldNames,
        locale: cardData.locale,
        transaction_type: cardData.transactionType,
        bill_to_email: customerData.email,
        reference_number: cardData.referenceNumber,
        ignore_avs: cardData.ignoreAvs,
        payer_authentication_challenge_code: cardData.payerAuthenticationChallengeCode,
        bill_to_address_country: customerData.addressCountry,
        bill_to_surname: customerData.surName,
        bill_to_address_line1: customerData.addressLine,
        profile_id: cardData.profileId,
        access_key: cardData.accessKey,
        bill_to_address_city: customerData.addressCity,
        currency: cardData.currency,
        bill_to_forename: customerData.firstName,
        signed_date_time: cardData.signedDateTime,
        customer_ip_address: cardData.customerIpAddress,
        customer_browser_screen_height: cardData.customerBrowserScreenHeight,
        customer_browser_screen_width: cardData.customerBrowserScreenWidth
    };

    if (cardData.transactionReason) {
        params.transaction_reason = cardData.transactionReason;
    }
    // Post to Iframe
    SgCardService.postToTarget('barclayCardIframe', cardData.hccUrl, params, setIsDataLoaded);
}

const goToPaymentErrorStep = (modelVM, error, caller, productCode, code) => {
    const paymentError = error || {};
    // Handle session timeout redirect, navigates to error t=2
    SgSessionService.handleSession(modelVM.value);
    // Handle payment errors, navigates to error t=10
    // Whilst the payment issues are ongoing the following will manually log an error in Dynatrace.
    // It will record the payment error code, what method was being called and the submission number.
    if (prodEnvironment) {
        let quoteID;
        if (modelVM.quoteID !== undefined) {
            quoteID = modelVM.quoteID.value;
        } else {
            quoteID = modelVM.policyNumber.value;
        }
        window.dtrum.reportError(`Payment Error ${code} from method ${caller} for submission ${quoteID}`);
    }
    if (code) {
        if (code === CommonConstants.SG_ERROR_CODES.SAGA_NON_PURCHASABLE_QUOTE) {
            paymentError.appErrorCode = CommonConstants.SG_ERROR_CODES.SAGA_NON_PURCHASABLE_QUOTE;
        }
    } else {
        paymentError.appErrorCode = CommonConstants.SG_ERROR_CODES.SAGA_PAYMENT_FAILED;
    }
    if (productCode) {
        paymentError.productCode = productCode;
    }
    throw paymentError;
};

function getPaymentResponse(modelVM, handleNext, useCardForRenewal) {
    const paymentError = {};
    const barclayCardDetails = modelVM.bindData.paymentDetails.barclayCardData_Ext.value;
    const quoteIDVal = modelVM.quoteID.value;
    SgCardService.retrieveBarclayCardResponse(quoteIDVal, barclayCardDetails.transactionUid, useCardForRenewal,
        ...customerDeviceDetails(barclayCardDetails))
        .then((resp) => {
            _.set(modelVM, 'bindData.paymentDetails.barclayCardResponse_Ext', resp);
            if (resp === CommonConstants.PAYMENT_RESPONSES.SUCCESS) {
                // GO TO CONF
                barclayCardDetails.hccUrl = null;
                handleNext();
            } else {
                throw resp;
            }
        })
        .catch((error) => {
            if (error === CommonConstants.PAYMENT_RESPONSES.REVIEW_AND_RETRY || error === CommonConstants.PAYMENT_RESPONSES.ALTERNATIVE_CARD) {
                // GO TO T16
                paymentError.appErrorCode = CommonConstants.SG_ERROR_CODES.SAGA_BARCLAYCARD_REVIEW_RETRY;
            } else if (error === CommonConstants.PAYMENT_RESPONSES.FRAUDULENT_CARD) {
                // GO TO T8
                paymentError.appErrorCode = CommonConstants.SG_ERROR_CODES.BARCLAYCARD_FRAUDULENT_CARD;
            } else {
                // Any thing else GO TO T10
                paymentError.appErrorCode = CommonConstants.SG_ERROR_CODES.SAGA_PAYMENT_FAILED;
            }
            throw paymentError;
        });
}

function buildBarclayCardRequest(modelVM, chosenPlan, handleNext, setShowFailedDownloadMsg, setShowCPA, setIsDataLoaded) {
    const customerData = buildCustomerData(modelVM);
    const quoteIDVal = modelVM.quoteID.value;
    const { paymentDetails } = modelVM.bindData;
    SgCardService.getBarclayCardDetails(quoteIDVal, !!_.get(modelVM, 'bindData.paymentDetails.barclayCardData_Ext.autoRenewal'),
        window.innerHeight, window.innerWidth).then((result) => {
        paymentDetails.barclayCardData_Ext = result;
        if (paymentDetails.barclayCardData_Ext.value.hasExistingResponseForJobNumber
                && paymentDetails.barclayCardData_Ext.value.hasExistingResponseForJobNumber === true) {
            setShowFailedDownloadMsg(true);
            getPaymentResponse(modelVM, handleNext, paymentDetails.barclayCardData_Ext.value.hasExistingCpaActive);
        } else {
            barclayCardIframe(paymentDetails.barclayCardData_Ext.value, customerData, undefined, setIsDataLoaded);
            if (isPaymentPlanAnnual(chosenPlan) && setShowCPA && SgQnBLobSpecificUtil.isCpaAvailable(modelVM)) {
                setShowCPA(true);
            }
        }
        if (modelVM.bindData.paymentDetails.barclayCardData_Ext !== undefined) {
            _.set(modelVM, 'bindData.paymentDetails.barclayCardData_Ext.hccUrl', null);
        }
    })
        .catch((error) => {
            goToPaymentErrorStep(modelVM, error, 'Get Barclaycard details');
        });
}

const bankAccountValidation = (modelVM, chosenPlan, handleNext, productCode) => {
    const { bankAccountData } = _.get(modelVM, 'bindData.paymentDetails') || modelVM;
    SgCardService.validateBankAccount(bankAccountData.sortCode_Ext.value, bankAccountData.bankAccountNumber.value,
        modelVM.value.quoteID, chosenPlan.installmentsSum)
        .then(() => handleNext())
        .catch((error) => {
            if (error.gwInfo.method === 'validateBankAccount') {
                _.set(error, 'customErrorMessage', 'Could not validate bank account');
                goToPaymentErrorStep(modelVM, error, 'DD validation', productCode);
            } else {
                throw error;
            }
        });
};

const loadBarclayCard = (modelVM, chosenPlan, handleNext, setShowFailedDownloadMsg, setShowCPA = undefined, setIsDataLoaded = () => {}) => {
    buildBarclayCardRequest(modelVM, chosenPlan, handleNext, setShowFailedDownloadMsg, setShowCPA, setIsDataLoaded);
};

const createPaymentInstrumentVM = (viewModelService) => {
    const viewModelContext = {};
    return viewModelService.create(
        {},
        'bc', 'edgev11.capabilities.billing.dto.PaymentInstrumentDTO',
        viewModelContext
    );
};

const getPaymentMethod = (paymentMethodCode, transactionCost) => {
    let paymentMethod;
    if (paymentMethodCode === CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD) {
        paymentMethod = CommonConstants.PAYMENT_METHOD_CODES.CREDIT_CARD;
    } else if (transactionCost.amount <= 0) {
        paymentMethod = CommonConstants.PAYMENT_METHOD_CODES.NO_PAYMENT_REQUIRED_PAYMENT_METHOD;
    } else {
        paymentMethod = CommonConstants.PAYMENT_METHOD_CODES.REDISTRIBUTE;
    }
    return paymentMethod;
};

const payWithExistingCard = (policyNumber, jobID, paymentInstrumentId, branchNumber, bind) => {
    let error;
    return SgCardService.payWithExistingCardBarclaycard(policyNumber, jobID, paymentInstrumentId, branchNumber)
        .then((result) => {
            if (result === CommonConstants.PAYMENT_RESPONSES.SUCCESS) {
                bind();
            } else {
                error = { customErrorMessage: result };
                throw error;
            }
        })
        .catch((err) => {
            error = { error: err, customErrorMessage: 'payment error' };
            throw error;
        });
};

const setMigratedPaymentDetails = (props) => {
    const {
        paymentInstrumentVM, setPaymentInstrumentVM, setPageName, pageName, setLoadingState
    } = props;
    setPaymentInstrumentVM(paymentInstrumentVM);
    if (pageName) {
        setPageName(pageName);
    }
    if (setLoadingState) {
        setLoadingState(false);
    }
};

const retrieveAndSetPaymentDetails = (props) => {
    const {
        policyNumber, paymentInstrumentVM, setPaymentInstrumentVM, existingPaymentInstrument,
        setPaymentDetails, setPageName, pageName, setLoadingState
    } = props;
    AccountBillingDetailsService.getInvoiceStreamDetailsWithLastPaymentInstrument(policyNumber).then((result) => {
        setPaymentDetails(result);
        const newPaymentInstrumentVM = _.clone(paymentInstrumentVM);
        _.set(newPaymentInstrumentVM, 'value', result.paymentInstrument);
        setPaymentInstrumentVM(newPaymentInstrumentVM);
        if (existingPaymentInstrument) {
            existingPaymentInstrument.current = _.cloneDeep(newPaymentInstrumentVM.value);
        }
        if (pageName) {
            setPageName(pageName);
        }
    }).catch((err) => {
        const error = { error: err, customErrorMessage: 'Could not fetch invoice stream details for policy' };
        throw error;
    }).finally(() => {
        if (setLoadingState) {
            setLoadingState(false);
        }
    });
};

const initPaymentDetails = (props) => {
    const { isMigrated } = props;
    if (isMigrated) {
        setMigratedPaymentDetails(props);
        return;
    }
    retrieveAndSetPaymentDetails(props);
};

const cancelUpdatingPaymentDetails = (props) => {
    const {
        setShowChangeDetailsForm, paymentInstrumentVM, setPaymentInstrumentVM, existingPaymentInstrument
    } = props;
    setShowChangeDetailsForm(false);
    const newPaymentInstrumentVM = _.clone(paymentInstrumentVM);
    newPaymentInstrumentVM.value = _.cloneDeep(existingPaymentInstrument.current);
    setPaymentInstrumentVM(newPaymentInstrumentVM);
};

const handleBarclayCardResponse = (props) => {
    const {
        event, barclayCardData, processBarclayCardResponse, autoRenewal
    } = props;
    const transactionUuid = event.data.TransactionUUID;
    const pageRedirectUrl = event.data.PageUrl && event.data.PageUrl.includes('payment-redirect.html?uuid=');
    if (pageRedirectUrl
        && transactionUuid !== undefined
        && transactionUuid.toString() === barclayCardData.transactionUid) {
        processBarclayCardResponse(autoRenewal, event.data.ResponseSaved, transactionUuid, ...customerDeviceDetails(barclayCardData));
    } else if (prodEnvironment && transactionUuid !== undefined) {
        // Logging to check status of barclaycard response
        window.dtrum.reportError(`Trigger process BCardResponse with UUID: ${event.data.TransactionUUID}`);
    }
};

const getProductPhoneNumberForDDI = () => {
    if (SgProductService.isSmi()) {
        return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_SMI_PHONE_NUMBER;
    }
    return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_HOME_PHONE_NUMBER;
};

const getPaymentType = (chosenPlan) => {
    return {
        isAnnualUpfrontPayment: (SgProductService.isSmi() || SgProductService.isTravel())
            && chosenPlan.billingId === CommonConstants.PAYMENT_PLANS_ID.ANNUAL_UPFRONT,
        isAnnualDDPayment: SgProductService.isSmi() && chosenPlan.billingId === CommonConstants.PAYMENT_PLANS_ID.ANNUAL_DD,
        isMonthlyDDPayment: SgProductService.isSmi() && chosenPlan.billingId === CommonConstants.PAYMENT_PLANS_ID.MONTHLY_DD,
        isQuarterlyDDPayment: SgProductService.isSmi() && chosenPlan.billingId === CommonConstants.PAYMENT_PLANS_ID.QUARTERLY_DD,
        isAnnualPayment: chosenPlan.numberOfInstallments_Ext <= 1,
        isMonthlyPayment: chosenPlan.numberOfInstallments_Ext > 1 && chosenPlan.downPayment.amount === 0,
        isMonthlyUpfrontPayment: chosenPlan.numberOfInstallments_Ext > 1 && chosenPlan.downPayment.amount > 0
    };
};

const shouldShowAnnualPaymentTypeQuestion = (chosenPlan) => {
    return SgProductService.isSmi() && getPaymentType(chosenPlan).isAnnualPayment;
};

const getFormattedSortCodeValue = (value) => {
    let sortCodeValue = value
        .replace(CommonConstants.REGEX_CONSTANTS.NON_NUMBERS,
            CommonConstants.REGEX_CONSTANTS.EMPTY_VALUE);
    if (sortCodeValue && sortCodeValue.length > 2) {
        sortCodeValue = sortCodeValue
            .match(CommonConstants.REGEX_CONSTANTS.TWO_ELEMENT_DIVISION)
            .join(CommonConstants.REGEX_CONSTANTS.DASH_SIGN);
    }
    return sortCodeValue;
};

export default {
    isIncreaseLessThanOrEqualsFivePound,
    isRefundLessThanOrEqualsFivePound,
    isAnnualCreditCardPayment,
    isAnnualPaymentPlan,
    isCreditCardPaymentMethod,
    isImmediatePaymentRequired,
    shouldShowCPAMessage,
    isQuarterlyCCPaymentPlan,
    loadBarclayCard,
    bankAccountValidation,
    getPaymentResponse,
    isDirectDebit,
    barclayCardIframe,
    createPaymentInstrumentVM,
    getPaymentMethod,
    payWithExistingCard,
    retrieveAndSetPaymentDetails,
    cancelUpdatingPaymentDetails,
    handleBarclayCardResponse,
    buildCustomerDataForIframe,
    customerDeviceDetails,
    getProductPhoneNumberForDDI,
    shouldShowAnnualPaymentTypeQuestion,
    getPaymentType,
    getFormattedSortCodeValue,
    initPaymentDetails
};
